import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '@/components/AppHeader';
import { PlayerNameLink } from '@/components/PlayerNameLink';
import { getPlayerParticipation } from '@/lib/api';
import { viewerHighlight } from '@/lib/viewerRow';
import { useAuth } from '@/contexts/AuthContext';
import type { PlayerParticipationResponse, TiebreakCriterion } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';
import { formatTiebreak, tiebreakLabel } from '@/lib/tiebreak-labels';

// Story 110 — public player participation page. Everything about one
// player's run in a tournament: identity, current standing, and the
// full round-by-round history with opponents linked to their own pages.

export function PlayerParticipationPage() {
  const { t } = useTranslation('tournament');
  const { slug, startingRank } = useParams<{ slug: string; startingRank: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<PlayerParticipationResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || !startingRank) return;
    setLoading(true);
    setNotFound(false);
    setData(null);
    getPlayerParticipation(slug, startingRank)
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, startingRank]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t('loading')}</p>
      </div>
    );
  }

  const config = data ? getGameConfig(data.game) : null;
  const showRating = config?.playerFields.rating ?? false;
  const isChess = data?.game === 'chess';
  const mine = viewerHighlight(data?.player.profileUserId ?? null, user?.id);

  return (
    <div data-testid="player-participation-page" className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-6">
        <div className="mb-6">
          {data && (
            <h2 className="text-xl md:text-2xl font-semibold mb-1">{data.tournamentName}</h2>
          )}
          <p data-testid="participation-label" className="text-sm text-muted-foreground mb-3">
            {t('participation.subtitle')}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to={`/t/${slug}`}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.info')}
            </Link>
            <Link
              to={`/t/${slug}/standings`}
              data-testid="standings-link"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.standings')}
            </Link>
            <Link
              to={`/t/${slug}/crosstable`}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.crosstable')}
            </Link>
          </div>
        </div>

        {notFound && (
          <p
            data-testid="participation-not-found"
            className="text-center text-muted-foreground"
          >
            {t('participation.not_found')}
          </p>
        )}

        {data && (
          <>
            <div
              data-testid="participation-header"
              data-viewer-row={mine.isMine ? 'true' : undefined}
              className={`mb-6 rounded border p-4 ${mine.className}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="text-lg font-semibold">
                  {`${data.player.firstName} ${data.player.lastName}`.trim()}
                </h3>
                {showRating && data.player.rating != null && (
                  <span className="text-sm text-muted-foreground">{data.player.rating}</span>
                )}
                {(data.player.status === 'withdrawn' ||
                  data.player.status === 'eliminated') && (
                  <span
                    data-testid="participation-status"
                    className="rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    <span data-testid="participation-label">
                      {data.player.status === 'withdrawn'
                        ? t('participation.status.withdrawn')
                        : t('participation.status.eliminated')}
                    </span>
                  </span>
                )}
              </div>
              {(data.player.club || data.player.city || data.player.country) && (
                <p className="mt-1 text-sm text-muted-foreground">
                  {[data.player.club, data.player.city, data.player.country]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </div>

            {data.standing && (
              <div data-testid="participation-standing" className="mb-6 rounded border p-4">
                <h4 data-testid="participation-label" className="mb-3 text-sm font-medium text-muted-foreground">
                  {t('participation.standing.title')}
                </h4>
                <div className="flex flex-wrap gap-x-8 gap-y-2">
                  <div>
                    <p data-testid="participation-label" className="text-xs text-muted-foreground">
                      {t('participation.standing.rank')}
                    </p>
                    <p data-testid="participation-rank" className="text-lg font-semibold">
                      {data.standing.rank}
                    </p>
                  </div>
                  <div>
                    <p data-testid="participation-label" className="text-xs text-muted-foreground">
                      {t('participation.standing.points')}
                    </p>
                    <p data-testid="participation-points" className="text-lg font-semibold">
                      {data.standing.points}
                    </p>
                  </div>
                  <div>
                    <p data-testid="participation-label" className="text-xs text-muted-foreground">
                      {t('participation.standing.games')}
                    </p>
                    <p className="text-lg font-semibold">{data.standing.gamesPlayed}</p>
                  </div>
                  {data.standing.byes > 0 && (
                    <div>
                      <p data-testid="participation-label" className="text-xs text-muted-foreground">
                        {t('participation.standing.byes')}
                      </p>
                      <p className="text-lg font-semibold">{data.standing.byes}</p>
                    </div>
                  )}
                  {data.standing.tiebreakCriteria.map((criterion: TiebreakCriterion) =>
                    data.standing!.tiebreaks[criterion] != null ? (
                      <div key={criterion}>
                        <p data-testid="participation-label" className="text-xs text-muted-foreground">
                          {tiebreakLabel(criterion, t)}
                        </p>
                        <p
                          data-testid={`participation-tiebreak-${criterion}`}
                          className="text-lg font-semibold"
                        >
                          {formatTiebreak(
                            criterion,
                            data.standing!.tiebreaks[criterion]!,
                          )}
                        </p>
                      </div>
                    ) : null,
                  )}
                </div>
              </div>
            )}

            <div className="overflow-x-auto rounded border">
              <table className="w-full text-left">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th data-testid="participation-label" className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">
                      {t('participation.history.round')}
                    </th>
                    <th data-testid="participation-label" className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">
                      {t('participation.history.table')}
                    </th>
                    <th data-testid="participation-label" className="px-2 py-2 text-xs font-medium text-muted-foreground">
                      {t('participation.history.opponent')}
                    </th>
                    {isChess && (
                      <th data-testid="participation-label" className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">
                        {t('participation.history.color')}
                      </th>
                    )}
                    <th data-testid="participation-label" className="w-20 px-2 py-2 text-xs font-medium text-muted-foreground">
                      {t('participation.history.result')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rounds.length === 0 && (
                    <tr>
                      <td colSpan={isChess ? 5 : 4} className="px-2 py-4 text-center text-sm text-muted-foreground">
                        <span data-testid="participation-label">
                          {t('participation.history.empty')}
                        </span>
                      </td>
                    </tr>
                  )}
                  {data.rounds.map((entry) => {
                    const opponentName = entry.opponent
                      ? `${entry.opponent.firstName} ${entry.opponent.lastName}`.trim()
                      : null;
                    return (
                      <tr
                        key={entry.roundNumber}
                        data-testid={`participation-round-${entry.roundNumber}`}
                        className="border-b last:border-b-0 hover:bg-muted/30"
                      >
                        <td className="px-2 py-2 text-sm">{entry.roundNumber}</td>
                        <td className="px-2 py-2 text-sm text-muted-foreground">
                          {entry.tableNumber ?? ''}
                        </td>
                        <td className="px-2 py-2 text-sm font-medium">
                          {entry.bye ? (
                            <span
                              data-testid="participation-bye"
                              className="text-muted-foreground"
                            >
                              <span data-testid="participation-label">
                                {t('participation.history.bye')}
                              </span>
                            </span>
                          ) : opponentName ? (
                            <>
                              <PlayerNameLink
                                slug={slug!}
                                startingRank={entry.opponent!.startingRank}
                                name={opponentName}
                              />
                              {entry.walkover && (
                                <span
                                  data-testid="participation-walkover"
                                  className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                                >
                                  <span data-testid="participation-label">
                                    {t('participation.history.walkover')}
                                  </span>
                                </span>
                              )}
                            </>
                          ) : (
                            ''
                          )}
                        </td>
                        {isChess && (
                          <td className="px-2 py-2 text-sm text-muted-foreground">
                            {entry.color === 'w'
                              ? t('participation.history.white')
                              : entry.color === 'b'
                                ? t('participation.history.black')
                                : ''}
                          </td>
                        )}
                        <td className="px-2 py-2 text-sm font-semibold">
                          {entry.result ? (
                            <span data-testid="participation-result">{entry.result}</span>
                          ) : (
                            <span data-testid="participation-label" className="font-normal text-muted-foreground">
                              {t('participation.history.pending')}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
